
import { transparentize } from 'polished';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { ObjectManager } from '../../../concepts/objects';
import { Camera } from '../../canvas/Camera';
import { EditorSession } from '../../canvas/EditorSession';
import { RootOverride } from '../../canvas/RootOverride';
import { Physical } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { Root } from '../../canvas/entities/Root';
import { TextAlignment } from '../../canvas/entities/TextConventions';
import { Button } from '../controls/Button';
import { Shape } from '../shapes/Shape';
import { ShapeKind } from '../shapes/ShapeEnums';
import { Text } from '../text/Text';
import { Colors } from './../../../ui';
import { PollController } from './PollController';
import { PollResults, PollSpec } from './PollTypes';

export const pollStart = defineCommand(
    'pollStart',
    'Poll: Start',
    (r, args: string[]) => {
        const spec = PollSpec.from(args);
        if (!!spec) {
            r.call(PollController).startFromSpec(spec);
        }
    },
);

export const pollFinish = defineCommand(
    'pollFinish',
    'Poll: Finish',
    r => {
        r.call(PollController).finish();
    }
);

export const pollToButton = defineCommand(
    'pollToButton',
    'Poll: To Button',
    (r, args: string[]) => {
        const spec = PollSpec.from(args);
        if (!spec) return;

        Physicals.createAtCameraPosition(r, Button, x => {
            x.width = 200;
            x.height = 50;
            x.content = 'Start Poll';
            x.commandScript = `pollStart ${args.map(a => ['"', a, '"'].join('')).join(' ')}`;
        });
    },
);

export const pollSave = defineCommand(
    'pollSave',
    'Poll: Save',
    r => {
        const state = r.call(PollController).getPollState();
        if (!state) return;
        const results = PollResults.from(state);

        const camera = r.call(Camera);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);

        const root = session.get(RootOverride) ?? objects.get('root', Root);
        const ncp = root.toRelative(camera.position.round(0)).round();

        const cl = (n: number) => Math.round(Math.ceil(n / 10) * 10);

        objects.update(u => {

            const f = u.create(Shape);
            f.background = '#ffffff';
            f.width = 500;
            f.height = 500;
            root.add(f);

            const x = 30;
            let y = 30;

            const q = u.create(Text);
            q.moveTo(x, y);
            q.autoHeight = false;
            q.fontColor = Colors.primaryText;
            q.fontFamily = 'Inter';
            q.fontSize = 18;
            q.content = state.question;
            q.width = f.width - (x * 2);
            q.sizeToContent();
            f.add(q);
            y += cl(q.height) + 10;

            for (const tm of results.items) {

                const o = u.create(Text);
                o.moveTo(x, y);
                o.fontColor = Colors.text;
                o.fontFamily = 'Inter';
                o.fontSize = 16;
                o.content = tm.option;
                o.sizeToContent();
                f.add(o);

                const v = u.create(Text);
                v.moveTo(x, y);
                v.fontColor = Colors.text;
                v.fontFamily = 'Inter';
                v.fontSize = 16;
                v.textAlignH = TextAlignment.end;
                v.content = tm.format === '%'
                    ? `${Math.round(tm.numer / tm.denom * 100) || 0}%`
                    : `${tm.numer || 0} / ${tm.denom || 0}`;
                v.sizeToContent();
                v.moveBy((f.width - (x * 2)) - v.width, 0);
                f.add(v);

                y += cl(v.height);

                const bv = u.create(Shape);
                bv.moveTo(x, y);
                bv.kind = ShapeKind.rectangle;
                bv.background = transparentize(.75, tm.color);
                bv.width = f.width - (x * 2);
                bv.height = 10;
                f.add(bv);

                const bb = u.create(Shape);
                bb.moveTo(x, y);
                bb.kind = ShapeKind.rectangle;
                bb.background = tm.color;
                bb.width = (f.width - (x * 2)) * (tm.numer / tm.denom) || 0;
                bb.height = 10;
                f.add(bb);

                y += cl(bb.height) + 10;

            }

            const { height } = Physicals.bounds(f.polarize(Physical));
            f.height = height + 60;
            f.moveTo(ncp.subtract(f.size().divide(2)));

        });

    }
);

export const pollRespond = defineCommand(
    'pollRespond',
    'Poll: Respond',
    (r, args: any[]) => {
        const answers = args.flatMap(x => x.toString().split(/,/g)).map(x => parseInt(x));
        r.call(PollController).respondWith(answers);
    },
    [
        stringStep('answer', 'What is your answer?')
    ]
);

export const inPollCommands = () => inCommands(
    pollStart,
    pollRespond,
);

